import React, { useCallback, useEffect, useState } from 'react';
import {
  atualizarSettingPortal,
  obterStatusOctadesk,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { StatusOctadesk } from '../../../interfaces/PortalCotacoes';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto de página compartilhado — import explícito, nunca depender de CSS carregado por
// outra tela (lição do BUG-V74, at-67).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Integrações com plataformas externas de mensagem (rota `/admin/sistema/integracoes`) — at-07.
 *
 * ## Por que é módulo próprio e não uma aba da Oficina
 *
 * Integração é transversal: o mesmo OctaDesk vai atender o convite de cotação (esta entrega),
 * o link de checklist e a pesquisa de satisfação (entrega 2). Como aba da Oficina, a
 * configuração de mensagens do Portal ficaria num lugar que não é dele.
 *
 * ## O que esta tela decide
 *
 * Um switch: o WhatsApp sai **pelo servidor** (número oficial da empresa) ou **pela mão de
 * quem opera** (`wa.me`, como sempre foi). Desligar é caminho de volta legítimo, e é a reação
 * certa quando a plataforma está com problema.
 *
 * ⚠️ **A carga que falha BLOQUEIA o switch** (regra da at-60): mostrar o switch em "desligada"
 * quando a consulta não respondeu seria transformar erro de API em afirmação de negócio — e
 * pior, um clique em seguida enviaria o estado errado. Aqui o estado só é editável quando
 * chegou íntegro.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela liga e desliga',
    conteudo: (
      <p>
        Com a integração <strong>ativa</strong>, o sistema envia as mensagens pelo número oficial da
        empresa, na hora em que a ação é feita. Com ela <strong>desligada</strong>, o sistema apenas
        prepara a mensagem e quem envia é a pessoa que está operando, pelo WhatsApp do próprio
        aparelho — que é como funcionava antes. Nenhuma das duas formas altera o que já foi enviado.
      </p>
    ),
  },
  {
    titulo: 'Por que o texto da mensagem não é editado aqui',
    conteudo: (
      <p>
        Mensagem enviada pela empresa para quem não está conversando com ela no momento só é
        entregue a partir de um <strong>modelo aprovado</strong> pela plataforma do WhatsApp. Esse
        modelo é cadastrado no painel do OctaDesk e aprovado por eles; aqui você informa apenas o{' '}
        <strong>código do modelo</strong>, e o sistema preenche as informações que variam (fornecedor,
        número da cotação, quantidade de itens, prazo e link). O texto editável que existe nas
        configurações de Cotações continua valendo para o e-mail e para o envio manual.
      </p>
    ),
  },
  {
    titulo: 'Por que o botão pode estar bloqueado',
    conteudo: (
      <p>
        Ativar exige duas coisas: as <strong>credenciais</strong> da plataforma configuradas neste
        ambiente e o <strong>código do modelo</strong> preenchido. Sem credenciais, quem resolve é a
        TI — elas não são preenchidas por esta tela, e é proposital: credencial não fica em campo de
        formulário. Sem o código do modelo, nenhuma mensagem conseguiria sair, então o sistema
        recusa a ativação em vez de deixar todo envio falhar depois.
      </p>
    ),
  },
  {
    titulo: 'O que acontece quando um envio falha',
    conteudo: (
      <p>
        A falha aparece na tela, com o motivo dado pela plataforma, e o envio{' '}
        <strong>não é refeito automaticamente</strong> por outro caminho. Enquanto a integração estiver
        ativa, não existe botão de envio manual para tentar em seguida — os dois nunca aparecem juntos
        (ver "O que esta tela liga e desliga"). Se a falha persistir, o aviso orienta pedir à TI que
        desative a integração aqui mesmo, o que devolve o envio manual imediatamente. Isso é deliberado
        — um envio que troca de caminho sozinho esconderia que a integração está fora do ar.
      </p>
    ),
  },
];

const IntegracoesPage: React.FC = () => {
  const [status, setStatus] = useState<StatusOctadesk | null>(null);
  const [erroCarga, setErroCarga] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [templateEditado, setTemplateEditado] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const dados = await obterStatusOctadesk();
      setStatus(dados);
      setTemplateEditado(dados.templateConvite);
      setErroCarga(false);
    } catch (error) {
      // Estado seguro + sinal visível: sem isto, a tela mostraria "desligada" para uma consulta
      // que não respondeu, e o switch enviaria o estado errado no primeiro clique.
      setStatus(null);
      setErroCarga(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar o estado da integração. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvarTemplate = async () => {
    setSalvando(true);
    try {
      await atualizarSettingPortal('OCTADESK_TEMPLATE_CONVITE', templateEditado.trim());
      notify.success('Código do modelo de mensagem salvo.');
      await carregar();
    } catch (error) {
      notify.actionError(error, { action: 'update', subject: 'modelo de mensagem' });
    } finally {
      setSalvando(false);
    }
  };

  const alternarIntegracao = async () => {
    if (!status) return;
    const proximo = !status.ativa;
    setSalvando(true);
    try {
      await atualizarSettingPortal('OCTADESK_ATIVO', proximo ? 'true' : 'false');
      notify.success(
        proximo
          ? 'Integração ativada. Os próximos envios sairão pelo número oficial da empresa.'
          : 'Integração desligada. Os próximos envios voltam a ser manuais.',
      );
      await carregar();
    } catch (error) {
      // O 409 do backend ("faltam credenciais", "falta o código do modelo") é o texto que
      // orienta — `actionError` prioriza a mensagem dele em 409.
      notify.actionError(error, { action: 'update', subject: 'estado da integração' });
    } finally {
      setSalvando(false);
    }
  };

  const templateMudou = status ? templateEditado.trim() !== status.templateConvite : false;
  const podeEditar = Boolean(status) && !erroCarga;

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      {(carregando || salvando) && <FullScreenLoader />}

      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <h1 className="recepcao-title">Integrações</h1>
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>

        {/*
          A faixa CONVIVE com o conteúdo em vez de substituí-lo (BUG-V27): montar a tela sob
          `!erro` apagaria título e ajuda justamente quando o usuário mais precisa deles.
        */}
        {erroCarga && (
          <p className="erro-de-tela">
            Não foi possível verificar o estado da integração. Enquanto isso, nada pode ser alterado
            aqui — recarregue a página; se persistir, acione a TI.
          </p>
        )}

        <div className="admin-card">
          <h2 className="admin-card-title">OctaDesk — envio de mensagens por WhatsApp</h2>

          {!erroCarga && status && !status.configurada && (
            <p className="aviso-de-tela">
              As credenciais da plataforma não estão configuradas neste ambiente, então a integração
              não pode ser ativada e os envios seguem manuais. Quem configura é a TI.
            </p>
          )}

          <p className="admin-hint">
            Situação: <strong>{descreverSituacao(status, erroCarga)}</strong>
          </p>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              className={`admin-btn ${status?.ativa ? 'admin-btn-danger-outline' : 'admin-btn-primary'}`}
              disabled={!podeEditar || salvando || (!status?.ativa && !status?.configurada)}
              onClick={alternarIntegracao}
            >
              {status?.ativa ? 'Desligar integração' : 'Ativar integração'}
            </button>
            {!podeEditar && !carregando && (
              <span className="admin-hint">Indisponível enquanto o estado não puder ser verificado.</span>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <label className="admin-hint" htmlFor="octadesk-template">
              Código ou ID do modelo de mensagem do convite (cadastrado no painel do OctaDesk)
            </label>
            <input
              id="octadesk-template"
              className="admin-input"
              type="text"
              value={templateEditado}
              disabled={!podeEditar || salvando}
              placeholder="ex: convite_cotacao"
              onChange={(e) => setTemplateEditado(e.target.value)}
            />
            <p className="admin-hint">
              O modelo precisa declarar os campos{' '}
              <strong>
                {(status?.variaveisEsperadas ?? []).join(', ') ||
                  'fornecedor, numero_cotacao, quantidade_de_itens, prazo_para_resposta, link_cotacao'}
              </strong>{' '}
              — são eles que o sistema preenche a cada envio. O nome tem de ser igual ao cadastrado
              no painel, senão a plataforma recusa a mensagem.
            </p>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={!podeEditar || salvando || !templateMudou}
              onClick={salvarTemplate}
            >
              Salvar código do modelo
            </button>
          </div>
        </div>
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Integrações"
        secoes={helpSections}
      />
    </div>
  );
};

/**
 * O texto que descreve a situação — e ele nunca afirma "desligada" quando a consulta falhou.
 * `efetiva` é o que decide o "ativa": um switch ligado sem credencial ou sem modelo não envia
 * nada, e chamar isso de ativa faria o comprador esperar um envio que não acontece.
 */
function descreverSituacao(status: StatusOctadesk | null, erro: boolean): string {
  if (erro || !status) return 'não foi possível verificar';
  if (!status.configurada) return 'não configurada neste ambiente';
  if (!status.ativa) return 'desligada — os envios são manuais';
  if (!status.templateConvite.trim()) return 'ativada, mas sem modelo de mensagem — os envios seguem manuais';
  return 'ativa — o sistema envia pelo número oficial da empresa';
}

export default IntegracoesPage;
